import { z } from 'zod';
export const platformStorageObjectReferenceCapability = 'platform.storage-object-reference.v1';
export const platformStoragePublicResourceDelegationCapability = 'platform.storage-public-resource-delegation.v1';
export const platformStorageUploadIntentIdSchema = z.string().uuid();
export const platformStorageObjectIdSchema = z.string().uuid();
export const platformStorageSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const platformStorageAccessScopeSchema = z.enum(['tenant', 'project']);
export const platformStorageObjectStateSchema = z.enum([
    'pending',
    'authorized',
    'verifying',
    'quarantined',
    'verified',
    'attached',
    'failed',
    'expired',
    'orphaned',
    'deleted',
]);
const platformStorageUrlSchema = z.string().url().superRefine((value, context) => {
    const url = new URL(value);
    const loopback = ['127.0.0.1', 'localhost', '::1'].includes(url.hostname);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) {
        context.addIssue({
            code: 'custom',
            message: 'Storage authorization URLs must use HTTPS outside loopback development.',
        });
    }
    if (url.username !== '' || url.password !== '') {
        context.addIssue({
            code: 'custom',
            message: 'Storage authorization URLs cannot contain embedded credentials.',
        });
    }
    if (url.hash !== '') {
        context.addIssue({
            code: 'custom',
            message: 'Storage authorization URLs cannot contain fragments.',
        });
    }
});
const platformStorageHeaderValueSchema = z.string().min(1).max(4_096).refine((value) => !/[\u0000-\u001f\u007f]/.test(value), 'Storage authorization header values cannot contain control characters.');
const platformStorageHeadersSchema = z
    .record(z.string().regex(/^[a-z0-9-]+$/), platformStorageHeaderValueSchema)
    .superRefine((headers, context) => {
    if (Object.keys(headers).length > 20) {
        context.addIssue({ code: 'custom', message: 'Storage authorization has too many headers.' });
    }
});
const platformStorageMetadataSchema = z
    .record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9]*$/), z.string().min(1).max(1_024))
    .superRefine((metadata, context) => {
    if (Object.keys(metadata).length > 20) {
        context.addIssue({ code: 'custom', message: 'Storage authorization has too much metadata.' });
    }
});
export const platformStorageUploadTransportSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('signed-url'),
        method: z.enum(['POST', 'PUT']),
        url: platformStorageUrlSchema,
        headers: platformStorageHeadersSchema,
        expiresAt: z.string().datetime({ offset: true }),
    }),
    z.strictObject({
        kind: z.literal('tus'),
        endpoint: platformStorageUrlSchema,
        headers: platformStorageHeadersSchema,
        metadata: platformStorageMetadataSchema,
        chunkSizeBytes: z.literal(6 * 1_024 * 1_024),
        expiresAt: z.string().datetime({ offset: true }),
    }),
]);
export const platformStorageUploadAuthorizationSchema = z.strictObject({
    transport: platformStorageUploadTransportSchema,
    providerAuthorizationId: z.string().trim().min(1).max(500),
});
export const platformStorageDetectedMediaMetadataSchema = z.strictObject({
    capturedAt: z.string().datetime({ offset: true }).nullable(),
    latitude: z.number().min(-90).max(90).nullable(),
    longitude: z.number().min(-180).max(180).nullable(),
    accuracyMeters: z.number().nonnegative().max(100_000).nullable(),
});
export const platformStorageObjectVerificationSchema = z.strictObject({
    actualSizeBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    actualContentType: z.string().trim().min(1).max(255),
    actualSha256: platformStorageSha256Schema,
    signatureMatchesContentType: z.boolean(),
    malwareStatus: z.enum(['clean', 'pending', 'detected', 'unavailable']),
    providerObjectVersion: z.string().trim().min(1).max(500).nullable(),
    mediaMetadata: platformStorageDetectedMediaMetadataSchema.nullable(),
});
export const platformStorageAttachedObjectReferenceSchema = z.strictObject({
    objectId: platformStorageObjectIdSchema,
    actualContentType: z.string().trim().min(1).max(255),
    actualSizeBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    actualSha256: platformStorageSha256Schema,
    verifiedAt: z.string().datetime({ offset: true }),
    attachedAt: z.string().datetime({ offset: true }),
});
export const platformStorageAttachedObjectReferenceRequestSchema = z.strictObject({
    objectId: platformStorageObjectIdSchema,
    moduleId: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/),
    resourceType: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    resourceId: z.string().uuid(),
    purpose: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/),
    requiredPermission: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
}).superRefine((value, context) => {
    if (!value.requiredPermission.startsWith(`${value.moduleId}.`)) {
        context.addIssue({
            code: 'custom',
            path: ['requiredPermission'],
            message: 'Storage object references require an owner-module permission.',
        });
    }
});
export const platformStorageDownloadAuthorizationSchema = z.strictObject({
    url: platformStorageUrlSchema,
    expiresAt: z.string().datetime({ offset: true }),
});
export const platformStorageObjectDeletionReceiptSchema = z.strictObject({
    providerDeletionId: z.string().trim().min(1).max(500),
    deletedAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=platform-storage.contract.js.map